import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, Film, Image as ImageIcon, Cloud, CheckCircle, Reply, Users } from 'lucide-react';
import MobileNav from '@/components/MobileNav';
import MentionInput from '@/components/MentionInput';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UploadPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const respondToId = searchParams.get('respondTo');
  const inspirationId = searchParams.get('inspiration');
  
  const [uploadedMedia, setUploadedMedia] = useState(null);
  const [caption, setCaption] = useState('');
  const [credits, setCredits] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cloudinaryConfig, setCloudinaryConfig] = useState(null);
  const [parentPost, setParentPost] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const widgetRef = useRef(null);

  useEffect(() => {
    fetchCloudinaryConfig();
    loadCloudinaryWidget();
    fetchUnreadCount();
    
    if (respondToId) {
      fetchParentPost(respondToId);
    }
  }, [respondToId]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  };

  const fetchParentPost = async (postId) => {
    try {
      const response = await axios.get(`${API}/feed`);
      const post = response.data.find(p => p.id === postId);
      if (post) setParentPost(post);
    } catch (error) {
      console.error('Failed to fetch parent post:', error);
    }
  };

  const fetchCloudinaryConfig = async () => {
    try {
      const response = await axios.get(`${API}/cloudinary/config`);
      setCloudinaryConfig(response.data);
    } catch (error) {
      console.error('Failed to get Cloudinary config:', error);
    }
  };

  const loadCloudinaryWidget = () => {
    if (window.cloudinary) return;
    
    const script = document.createElement('script');
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    document.body.appendChild(script);
  };

  const openUploadWidget = async () => {
    if (!cloudinaryConfig || !window.cloudinary) {
      toast.error('Upload widget not ready');
      console.error('Widget not ready:', { cloudinaryConfig, cloudinary: !!window.cloudinary });
      return;
    }

    console.log('Opening upload widget with config:', cloudinaryConfig);

    // Build widget config - signature function must use callback pattern
    const widgetConfig = {
      cloudName: cloudinaryConfig.cloud_name,
      apiKey: cloudinaryConfig.api_key,
      uploadSignature: (callback, paramsToSign) => {
        console.log('Requesting signature for:', paramsToSign);
        axios.post(`${API}/cloudinary/sign`, paramsToSign)
          .then(response => {
            console.log('Signature response:', response.data);
            callback(response.data);
          })
          .catch(error => {
            console.error('Failed to get signature:', error);
            toast.error('Signature failed');
            callback(null);
          });
      },
      folder: 'zombify/posts',
      sources: ['local', 'camera'],
      multiple: false,
      maxFileSize: 500000000,
      resourceType: 'auto',
      clientAllowedFormats: ['image', 'video'],
      showAdvancedOptions: false,
      cropping: false,
      styles: {
        palette: {
          window: "#0A0A0A",
          windowBorder: "#FFDD00",
          tabIcon: "#FFDD00",
          menuIcons: "#ffffff",
          textDark: "#000000",
          textLight: "#ffffff",
          link: "#FFDD00",
          action: "#2B5CE4",
          inactiveTabIcon: "#6b7280",
          error: "#E53935",
          inProgress: "#2B5CE4",
          complete: "#FFDD00",
          sourceBg: "#0A0A0A"
        }
      }
    };

    const widget = window.cloudinary.createUploadWidget(
      widgetConfig,
      (error, result) => {
        console.log('Widget callback:', { error, event: result?.event, info: result?.info });
        
        if (error) {
          console.error('Upload error:', error);
          toast.error('Upload failed: ' + (error.message || 'Unknown error'));
          return;
        }
        
        if (result && result.event === "success") {
          const info = result.info;
          setUploadedMedia({
            url: info.secure_url,
            public_id: info.public_id,
            resource_type: info.resource_type,
            format: info.format,
            width: info.width,
            height: info.height,
            duration: info.duration
          });
          toast.success('MEDIA UPLOADED!');
        }
        if (error) {
          console.error('Upload error:', error);
          toast.error('UPLOAD FAILED');
        }
      }
    );

    widgetRef.current = widget;
    widget.open();
  };

  const handleSubmit = async () => {
    if (!uploadedMedia) {
      toast.error('UPLOAD MEDIA FIRST');
      return;
    }

    setSubmitting(true);
    try {
      const creditUsernames = credits.match(/@(\w+)/g)?.map(m => m.slice(1)) || [];
      
      await axios.post(`${API}/posts/upload/cloudinary`, {
        caption: caption,
        cloudinary_url: uploadedMedia.url,
        cloudinary_public_id: uploadedMedia.public_id,
        resource_type: uploadedMedia.resource_type,
        credits: [],
        parent_post_id: respondToId || null,
        inspiration_id: inspirationId || null
      });

      toast.success('SUBMISSION UPLOADED!');
      navigate('/centrum');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'UPLOAD FAILED');
    } finally {
      setSubmitting(false);
    }
  };

  const removeMedia = () => {
    setUploadedMedia(null);
  };

  return (
    <div className="min-h-screen bg-zom-blue text-zom-white pt-20 pb-24">
      <div className="p-4 space-y-6 max-w-lg mx-auto">
        {/* Responding to */}
        {parentPost && (
          <div className="bg-zom-black border-3 border-zom-yellow p-4">
            <div className="flex items-center gap-2 text-sm text-zom-yellow font-heading mb-2">
              <Reply className="w-4 h-4" strokeWidth={2.5} />
              ODPOWIADASZ NA POST @{parentPost.performer_email?.split('@')[0].toUpperCase()}
            </div>
            {parentPost.caption && (
              <p className="text-sm text-zom-white/70 truncate font-body">{parentPost.caption}</p>
            )}
          </div>
        )}

        {/* Upload Area */}
        <div className="space-y-4">
          {!uploadedMedia ? (
            <button
              onClick={openUploadWidget}
              className="w-full aspect-[9/16] max-h-[400px] border-4 border-dashed border-zom-white flex flex-col items-center justify-center gap-4 hover:border-zom-yellow hover:bg-zom-yellow/5 transition-all"
            >
              <div className="w-20 h-20 bg-zom-black flex items-center justify-center border-3 border-zom-yellow">
                <Cloud className="w-10 h-10 text-zom-yellow" strokeWidth={2.5} />
              </div>
              <div className="text-center">
                <p className="font-heading tracking-wide">KLIKNIJ ABY WYBRAĆ</p>
                <p className="text-sm text-zom-white/60 mt-1 font-body">WIDEO LUB ZDJĘCIE</p>
              </div>
            </button>
          ) : (
            <div className="relative aspect-[9/16] max-h-[400px] bg-zom-black border-4 border-zom-white overflow-hidden">
              {uploadedMedia.resource_type === 'video' ? (
                <video 
                  src={uploadedMedia.url} 
                  className="w-full h-full object-cover"
                  controls
                />
              ) : (
                <img 
                  src={uploadedMedia.url} 
                  alt="Uploaded" 
                  className="w-full h-full object-cover"
                />
              )}
              <button
                onClick={removeMedia}
                className="absolute top-2 right-2 p-2 bg-zom-red border-2 border-zom-black hover:bg-zom-black hover:text-zom-red transition-colors"
              >
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>
              <div className="absolute bottom-2 left-2 flex items-center gap-2 bg-zom-black border-2 border-zom-yellow px-3 py-1">
                {uploadedMedia.resource_type === 'video' ? (
                  <Film className="w-4 h-4 text-zom-yellow" strokeWidth={2.5} />
                ) : (
                  <ImageIcon className="w-4 h-4 text-zom-yellow" strokeWidth={2.5} />
                )}
                <span className="text-sm font-heading">
                  {uploadedMedia.resource_type === 'video' 
                    ? `${Math.round(uploadedMedia.duration || 0)}S` 
                    : `${uploadedMedia.width}x${uploadedMedia.height}`}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Caption */}
        <div>
          <label className="text-xs text-zom-yellow font-heading mb-2 block tracking-wide">TEKST (OPCJONALNIE)</label>
          <MentionInput
            value={caption}
            onChange={setCaption}
            placeholder="Dodaj opis, możesz tagować @użytkowników..."
            rows={3}
          />
        </div>

        {/* Credits */}
        <div>
          <label className="text-xs text-zom-yellow font-heading mb-2 flex items-center gap-2 tracking-wide">
            <Users className="w-4 h-4" strokeWidth={2.5} />
            CREDITS - KTO BRAŁ UDZIAŁ?
          </label>
          <MentionInput
            value={credits}
            onChange={setCredits}
            placeholder="@performer1 @performer2..."
            rows={2}
          />
          <p className="text-xs text-zom-white/50 mt-1 font-body">
            Oznacz innych performerów
          </p>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!uploadedMedia || submitting}
          className="w-full py-4 bg-zom-yellow text-zom-black font-heading text-lg tracking-wide border-3 border-zom-black shadow-brutal hover:shadow-brutal-lg transition-shadow disabled:opacity-50"
        >
          {submitting ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-zom-black border-t-transparent animate-spin" />
              WYSYŁANIE...
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5" strokeWidth={2.5} />
              WYŚLIJ DO REVIEW
            </div>
          )}
        </button>

        <p className="text-xs text-zom-white/50 text-center font-body">
          TWÓJ UPLOAD ZOSTANIE PRZEJRZANY PRZEZ WRITERA PRZED PUBLIKACJĄ
        </p>
      </div>

      <MobileNav unreadCount={unreadCount} />
    </div>
  );
};

export default UploadPage;
